import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../auth/AuthContext";
import { UserRole } from "../../types/User";

type Placement = "top" | "bottom";

type TourStep = {
  target: string;
  content: string;
  placement: Placement;
  route?: string;
  pageId?: string;
  requiresAdmin?: boolean;
  title?: string;
};

type HighlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type TooltipPosition = {
  top: number;
  left: number;
};

const TOOLTIP_WIDTH = 420;
const TOOLTIP_MARGIN = 16;
const HIGHLIGHT_PADDING = 10;
const VIEWPORT_PADDING = 16;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const OnboardingTour = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [highlightRect, setHighlightRect] = useState<HighlightRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);

  const hiddenPages = useMemo(
    () => (Array.isArray(user?.tenant?.hiddenPages) ? user.tenant.hiddenPages : []),
    [user?.tenant?.hiddenPages]
  );
  const isAdmin =
    user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN;

  const steps = useMemo<TourStep[]>(() => {
    const raw: TourStep[] = [
      {
        target: '[data-tour="dashboard-kpis"]',
        content: t("tourDashboardOverview"),
        placement: "bottom",
        route: "/",
        pageId: "dashboard",
      },
      {
        target: '[data-tour="dashboard-call-list"]',
        content: t("tourCallListDoubleClick"),
        placement: "top",
        route: "/",
        pageId: "dashboard",
      },
      {
        target: '[data-tour="todo-list"]',
        content: t("tourTodoListDoubleClick"),
        placement: "top",
        route: "/todos",
        pageId: "todos",
      },
      {
        target: '[data-tour="todo-lock-info"]',
        content: t("tourTodoLockInfo"),
        placement: "top",
        route: "/todos",
        pageId: "todos",
      },
      {
        target: '[data-tour="user-management"]',
        content: t("tourUserManagement"),
        placement: "top",
        route: "/users",
        pageId: "users",
        requiresAdmin: true,
      },
    ];

    return raw.filter((step) => {
      if (step.requiresAdmin && !isAdmin) return false;
      if (step.pageId && hiddenPages.includes(step.pageId)) return false;
      return true;
    });
  }, [hiddenPages, isAdmin, t]);

  useEffect(() => {
    if (!user?._id) return;
    const key = `onboarding_tour_v1_done_${user._id}`;
    const done = localStorage.getItem(key) === "1";
    if (done) return;
    if (location.pathname !== "/") return;
    if (!steps.length) return;
    // Mark as seen on first run to prevent showing again after logout/login.
    localStorage.setItem(key, "1");
    setStepIndex(0);
    setRun(true);
  }, [location.pathname, steps.length, user?._id]);

  useEffect(() => {
    if (pendingIndex === null) return;
    const targetStep = steps[pendingIndex];
    if (!targetStep?.route || location.pathname === targetStep.route) {
      setStepIndex(pendingIndex);
      setPendingIndex(null);
      setRun(true);
    }
  }, [location.pathname, pendingIndex, steps]);

  const finishTour = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    setPendingIndex(null);
    setHighlightRect(null);
    setTooltipPosition(null);
    if (location.pathname !== "/") {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  const goToStep = useCallback((nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= steps.length) {
      finishTour();
      return;
    }

    const nextStep = steps[nextIndex];
    if (nextStep?.route && location.pathname !== nextStep.route) {
      setRun(false);
      setPendingIndex(nextIndex);
      navigate(nextStep.route);
      return;
    }

    setStepIndex(nextIndex);
  }, [finishTour, location.pathname, navigate, steps]);

  useLayoutEffect(() => {
    if (!run || !steps.length) {
      setHighlightRect(null);
      setTooltipPosition(null);
      return;
    }

    const currentStep = steps[stepIndex];
    if (!currentStep) {
      finishTour();
      return;
    }

    let frameId = 0;

    const updatePosition = () => {
      const targetElement = document.querySelector(currentStep.target);

      if (!(targetElement instanceof HTMLElement)) {
        goToStep(stepIndex + 1);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      const paddedRect: HighlightRect = {
        top: Math.max(rect.top - HIGHLIGHT_PADDING, VIEWPORT_PADDING),
        left: Math.max(rect.left - HIGHLIGHT_PADDING, VIEWPORT_PADDING),
        width: rect.width + HIGHLIGHT_PADDING * 2,
        height: rect.height + HIGHLIGHT_PADDING * 2,
      };

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const tooltipLeft = clamp(
        rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
        VIEWPORT_PADDING,
        Math.max(VIEWPORT_PADDING, viewportWidth - TOOLTIP_WIDTH - VIEWPORT_PADDING)
      );

      const estimatedTooltipHeight = 220;
      const preferredTop =
        currentStep.placement === "top"
          ? rect.top - estimatedTooltipHeight - TOOLTIP_MARGIN
          : rect.bottom + TOOLTIP_MARGIN;
      const fallbackTop =
        currentStep.placement === "top"
          ? rect.bottom + TOOLTIP_MARGIN
          : rect.top - estimatedTooltipHeight - TOOLTIP_MARGIN;

      let tooltipTop = preferredTop;
      if (tooltipTop < VIEWPORT_PADDING || tooltipTop > viewportHeight - estimatedTooltipHeight - VIEWPORT_PADDING) {
        tooltipTop = fallbackTop;
      }
      tooltipTop = clamp(
        tooltipTop,
        VIEWPORT_PADDING,
        Math.max(VIEWPORT_PADDING, viewportHeight - estimatedTooltipHeight - VIEWPORT_PADDING)
      );

      setHighlightRect(paddedRect);
      setTooltipPosition({ top: tooltipTop, left: tooltipLeft });

      targetElement.scrollIntoView({
        block: currentStep.placement === "top" ? "end" : "center",
        behavior: "smooth",
      });
    };

    updatePosition();

    const handleChange = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updatePosition);
    };

    window.addEventListener("resize", handleChange);
    window.addEventListener("scroll", handleChange, true);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleChange);
      window.removeEventListener("scroll", handleChange, true);
    };
  }, [finishTour, goToStep, location.pathname, run, stepIndex, steps]);

  if (!run || !steps.length || !highlightRect || !tooltipPosition) {
    return null;
  }

  const currentStep = steps[stepIndex];
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100);
  const isLastStep = stepIndex + 1 === steps.length;
  const bubbleBg = "rgba(31, 41, 55, 0.92)";

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundColor: "rgba(17, 24, 39, 0.55)",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: highlightRect.top,
          left: highlightRect.left,
          width: highlightRect.width,
          height: highlightRect.height,
          borderRadius: 14,
          boxShadow: "0 0 0 9999px rgba(17, 24, 39, 0.55)",
          border: "2px solid rgba(255, 255, 255, 0.9)",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: TOOLTIP_WIDTH,
          maxWidth: `calc(100vw - ${VIEWPORT_PADDING * 2}px)`,
          borderRadius: 12,
          overflow: "hidden",
          backgroundColor: bubbleBg,
          boxShadow: "0 12px 28px rgba(0, 0, 0, 0.25)",
          pointerEvents: "auto",
        }}
      >
        <div style={{ height: 4, backgroundColor: "rgba(255, 255, 255, 0.15)" }}>
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              backgroundColor: "#3d3b8e",
              transition: "width 200ms ease",
            }}
          />
        </div>
        <div style={{ padding: "16px 18px 14px", position: "relative" }}>
          <button
            type="button"
            onClick={finishTour}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "transparent",
              border: "none",
              color: "rgba(255, 255, 255, 0.7)",
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ✕
          </button>
          {currentStep.title && (
            <div style={{ fontWeight: 600, marginBottom: 8, color: "#fff" }}>{currentStep.title}</div>
          )}
          <div style={{ color: "rgba(255, 255, 255, 0.92)", lineHeight: 1.5 }}>
            {currentStep.content}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "0 16px 14px",
          }}
        >
          <button
            type="button"
            onClick={finishTour}
            style={{
              background: "transparent",
              border: "none",
              color: "rgba(255, 255, 255, 0.6)",
              cursor: "pointer",
            }}
          >
            {t("tourSkip")}
          </button>
          <div style={{ flex: 1 }} />
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={() => goToStep(stepIndex - 1)}
              style={{
                background: "transparent",
                border: "none",
                color: "rgba(255, 255, 255, 0.7)",
                cursor: "pointer",
              }}
            >
              {t("tourBack")}
            </button>
          )}
          <button
            type="button"
            onClick={() => goToStep(stepIndex + 1)}
            style={{
              backgroundColor: "#3d3b8e",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "8px 14px",
              cursor: "pointer",
            }}
          >
            {isLastStep ? t("tourDone") : t("tourNext")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingTour;
